import TierBadge from '../components/TierBadge.jsx'
import { releases } from '../content.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// /releases — P6-85. The page the nav has been pointing at since P6-104.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT THIS RENDERS, AND WHAT IT STRUCTURALLY CANNOT
// ═══════════════════════════════════════════════════════════════════════════
//
// `releases.entries` is the PUBLISHED half of site/content-src/releases/*.md.
// A withheld draft is not filtered out here — it never enters the document this
// imports, because scripts/generate-content.mjs builds that document from the
// published list alone. There is no `status` check in this file and there must
// never be one: a filter is a line somebody can edit wrongly, and an absence is
// not.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ONE PAGE, NEWEST FIRST — NOT A PAGE PER RELEASE
// ═══════════════════════════════════════════════════════════════════════════
//
// A route per release would need a registry entry per release, which makes
// "publish a release" a two-file change and puts a rotting list in
// sections.json. P6-85 asks for the opposite: "2.0 ships as a release week, not
// a single post. Structure it so later posts are cheap." Cheap means one file,
// dropped in a directory, and nothing else — which is what this shape buys.
//
// It also reads better for the audience the corrected design brief names: six
// people on their phones, in Discord, for ten seconds. A changelog is a thing
// you scroll, not a thing you navigate.
//
// Revisit at roughly ten releases, when the scroll stops being the shorter path.

const DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  // UTC, explicitly, for directory.js's reason one feature over: a release date
  // is a fact about the repo, not about the reader, and letting the browser's
  // zone decide gives six people two different answers either side of midnight.
  timeZone: 'UTC',
})

function dayLabel(iso) {
  return DAY.format(new Date(`${iso}T00:00:00Z`))
}

/** One inline run. `code` and `strong` are the only two spans the parser emits,
 * and an unknown kind renders as its text rather than as nothing — this is the
 * one place a future span type should degrade rather than disappear, because
 * the alternative is a published sentence with a word missing. The parser is
 * where an unsupported construct is refused; by here it is too late to refuse
 * anything, so the honest move is to show the words. */
function Span({ span }) {
  if (span.kind === 'code') return <code className="d2d-release__code">{span.value}</code>
  if (span.kind === 'strong') return <strong>{span.value}</strong>
  return span.value
}

function Spans({ spans }) {
  return spans.map((span, i) => <Span key={i} span={span} />)
}

function Block({ block }) {
  if (block.kind === 'list') {
    return (
      <ul className="d2d-release__list">
        {block.items.map((item, i) => (
          <li key={i}>
            <Spans spans={item} />
          </li>
        ))}
      </ul>
    )
  }
  return (
    <p>
      <Spans spans={block.spans} />
    </p>
  )
}

function Release({ release }) {
  return (
    <article
      className="d2d-release"
      aria-labelledby={release.anchor}
      data-testid={`release-${release.id}`}
    >
      {/* The heading carries the anchor, so the skim rail's href and the
          landmark's label are the same element rather than two that have to be
          kept in step. */}
      <h2 id={release.anchor}>{release.title}</h2>
      <p className="d2d-release__meta d2d-micro">
        <time dateTime={release.date}>{dayLabel(release.date)}</time> · Version {release.version}
      </p>
      <p className="d2d-release__summary">{release.summary}</p>

      {release.sections.map((section) => (
        <section key={section.id} aria-labelledby={section.anchor}>
          <h3 id={section.anchor}>{section.title}</h3>
          {section.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </section>
      ))}
    </article>
  )
}

/**
 * 🔴 THE EMPTY STATE IS THE PAGE'S FIRST REAL STATE, AND IT IS HONEST RATHER
 * THAN A PROMISE.
 *
 * Three options were on the table and two of them publish something untrue.
 * Backfilling releases from docs/ledger.md would invent announcements that were
 * never made — P6-85's own rule is "announcement-semantics: a release is what we
 * tell people, not every merge", and a changelog reconstructed after the fact is
 * exactly the confident, plausible, false artefact this repo's entire gotcha
 * list is about. Leaving the section a `slot` would claim the page is not built
 * when the mechanism behind it is finished and running.
 *
 * So: a real page, with nothing announced yet, saying so in one flat sentence.
 * That is Slot.jsx's own empty-state rule — "one flat sentence, at most one dry
 * follow-up, no apology" — applied to a page that has a mechanism rather than a
 * blocker.
 *
 * ⚠️ It names no unpublished release. A draft's TITLE is the announcement, and
 * "2.0 is coming" is a smaller leak than the headline but a leak of the same
 * fact. The page says what is true — nothing yet — and nothing about what is
 * queued behind it.
 */
function EmptyState() {
  return (
    <p className="d2d-notice" data-testid="releases-empty">
      Nothing has been announced yet. When something is, it lands here — one post per release,
      newest first.
    </p>
  )
}

export default function Releases() {
  useDocumentTitle('Release notes')
  const entries = releases.entries

  // 🔴 THE RAIL LISTS RELEASES, UNLESS THERE IS ONLY ONE — then it lists that
  // release's own sections. A one-item table of contents is furniture, and the
  // first real state of this page is exactly one long post: P6-85's baseline is
  // pre-audit July, so the 2.0 note covers about six weeks and is the longest
  // thing on the site. On a phone that post needs a rail more than the page
  // needs a list of one.
  const railItems =
    entries.length > 1
      ? entries.map((entry) => ({ anchor: entry.anchor, title: entry.title }))
      : (entries[0]?.sections ?? []).map((section) => ({
          anchor: section.anchor,
          title: section.title,
        }))

  return (
    <article className="d2d-page">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">
          <TierBadge tier={1} />
        </p>
        <h1 className="d2d-titleblock__headline">Release notes</h1>
        <p className="d2d-titleblock__lede">
          What changed, and when. Not every merge — the things worth telling people about.
        </p>
        <hr className="d2d-rule" />
        {/* 🔴 ONE PLATFORM VERSION, and it is derived from the newest published
            release rather than declared anywhere. P6-85: "one platform version
            across the deployables", so there is deliberately no per-service
            number to render and nowhere to write one. Absent until something
            publishes — an unversioned platform says nothing rather than "0.0". */}
        {releases.version === null ? null : (
          <p className="d2d-titleblock__meta d2d-micro" data-testid="platform-version">
            Platform version {releases.version}
          </p>
        )}
      </header>

      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="d2d-doc">
          <nav className="d2d-skimrail" aria-label="On this page">
            <p className="d2d-footer__head d2d-micro">On this page</p>
            <ul>
              {railItems.map((item) => (
                <li key={item.anchor}>
                  <a className="d2d-skimrail__link" href={`#${item.anchor}`}>
                    {item.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="d2d-prose">
            {entries.map((release) => (
              <Release key={release.id} release={release} />
            ))}
          </div>
        </div>
      )}
    </article>
  )
}
